import { useBranchLimits } from "@tlc/ls2pac/components/BranchLimits";
import { useLanguage } from "@tlc/ls2pac/components/Language";
import { useLocalHoldingsBranch } from "../../../resource/LocalHoldingsBranch";
import { ReactNode, useMemo } from "react";
import { useItemAvailability } from "../../../resource/ItemAvailability";
import React from "react";
import { Tooltip, Typography, Box, styled } from "@mui/material";
import { Link } from "react-router-dom";
import { LSResultItemAvailabilitySkeleton } from "./LSResultItemSkeleton";
import { useVisibleHoldings } from "../../../resource/useVisibleHoldings";
import { ResourceView } from "@tlc/ls2pac/dto/ResourceView";
import { ResourceDWR } from "@tlc/ls2pac/dto/ResourceDWR";

function AvailabilityStatement({
  dense = false,
  count,
  availableStatus,
  severity,
  locationPreposition,
  locationName,
  viewLink,
}: {
  dense?: boolean;
  count?: number;
  availableStatus: string;
  severity: "error" | "info" | "success";
  locationPreposition?: string;
  locationName?: string;
  viewLink: string;
}) {
  const { translate } = useLanguage();

  const at = locationPreposition ?? translate("availability_at");
  const availabilityText = count
    ? `${count} ${availableStatus}`
    : availableStatus;
  const viewLinkDisplay =
    viewLink && viewLink.length > 0 ? ` (${viewLink})` : "";
  const tooltip =
    dense &&
    `${availabilityText}${
      locationName ? ` ${at} ${locationName}${viewLinkDisplay}` : ""
    }`;

  if (dense) {
    return (
      <Tooltip title={tooltip}>
        <Box>
          <Typography
            tabIndex={0}
            component="span"
            color={severity}
            fontWeight="bold"
          >
            {availabilityText}
          </Typography>
          {viewLink && (
            <Typography component="span" className="copies-link-text">
              {` (${translate("view")})`}
            </Typography>
          )}
        </Box>
      </Tooltip>
    );
  }

  return (
    <Typography component="span" sx={{ mr: 0.5 }}>
      <Typography component="span" color={severity} fontWeight="bold">
        {availabilityText}
      </Typography>
      {locationName && (
        <>
          <Typography component="span"> {at} </Typography>
          <Typography component="span" fontWeight="bold">
            {locationName}
          </Typography>
        </>
      )}

      {viewLink && (
        <Typography component="span" className="copies-link-text">
          {` (${viewLink})`}
        </Typography>
      )}
    </Typography>
  );
}

interface LSResultItemAvailabilityProps {
  resource: ResourceDWR | ResourceView;
  dense?: boolean;
  reserves?: boolean;
}
export const LSResultItemAvailability = ({
  resource,
  dense = false,
  reserves = false,
}: LSResultItemAvailabilityProps) => {
  const { translate } = useLanguage();
  const { branchLimits, selectedBranchIds, displayInCopiesBranchIds } =
    useBranchLimits();
  const { branchIdentifier: localHoldingsBranch } = useLocalHoldingsBranch();
  const visibleHoldings = useVisibleHoldings(resource, reserves);

  //   sync TODO move shelfLocationBranch to a hook or context
  const shelfLocationBranch = useMemo(() => {
    if ((selectedBranchIds ?? []).length === 1) return selectedBranchIds[0];
    if (localHoldingsBranch) return localHoldingsBranch; // ignored if selectedBranchIds.length > 0
    return null;
  }, [localHoldingsBranch, selectedBranchIds]);

  //   sync
  const holdings = useMemo(() => {
    if (selectedBranchIds?.length) {
      // limit to copies at selected locations
      // this is likely already happening via the /search endpoint
      return visibleHoldings.filter((hi) =>
        selectedBranchIds.includes(hi.branchIdentifier),
      );
    }

    // TODO this seems bad
    // Instead of silently resolving this branch we should select it in the filter (This Location or Your Location)
    // limit to copies at the resolved shelf location
    if (shelfLocationBranch) {
      return visibleHoldings.filter(
        (hi) => hi.branchIdentifier === shelfLocationBranch,
      );
    }

    // limit to copies displayed by the config
    // this is hopefully already happening via the /search endpoint
    return visibleHoldings;
  }, [visibleHoldings, shelfLocationBranch, selectedBranchIds]);

  const shelfLocationBranchName = useMemo(() => {
    const branchLimit = (branchLimits || []).find(
      (bl) => bl.identifier === shelfLocationBranch,
    );
    return branchLimit ? branchLimit.displayName : null;
  }, [shelfLocationBranch, branchLimits]);

  //   async
  const { itemAvailability, ready } = useItemAvailability(holdings, resource);

  const whereText = useMemo(() => {
    if (selectedBranchIds.length > 0) return null; // we don't display location names when filtering
    if (shelfLocationBranch) {
      return shelfLocationBranchName;
    }
    return translate("availability_allLocations");
  }, [
    selectedBranchIds,
    shelfLocationBranch,
    shelfLocationBranchName,
    translate,
  ]);

  const availabilityLink: ReactNode = useMemo(() => {
    if (!ready) {
      return null;
    }
    const itemAvailabilities = Array.from(itemAvailability.values());
    const hasCopies = itemAvailabilities.length > 0;
    let status = "";
    let severity: "error" | "info" | "success" = "info";
    let count: number | undefined = undefined;

    if (!hasCopies) {
      // Handles both serial no-copies and standard no-copies
      status = resource.serial
        ? translate("serialMessage")
        : translate("noCopies");
      severity = "info";
    } else {
      const countAvailable = itemAvailabilities.filter(
        (ia) => ia.available && !ia.nonCirculating && !ia.onOrder,
      ).length;
      const countNonCirculating = itemAvailabilities.filter(
        (ia) => ia.nonCirculating,
      ).length;
      const onOrderCount = itemAvailabilities.filter((ia) => ia.onOrder).length;

      if (countAvailable > 0) {
        count = countAvailable;
        status = translate("available");
        severity = "success";
      } else if (countNonCirculating > 0) {
        count = countNonCirculating;
        status = translate("nonCirculating");
        severity = "success";
      } else if (onOrderCount > 0) {
        count = onOrderCount;
        status = translate("onOrder");
        severity = "error";
      } else {
        status = translate("notAvailable");
        severity = "error";
      }
    }

    let viewLink: string = "";
    const linkText =
      selectedBranchIds.length > 0
        ? translate("availability_viewSelectedLocations")
        : shelfLocationBranch
        ? translate("availability_checkOtherLocations")
        : translate("availability_viewAllLocations");

    if (displayInCopiesBranchIds.length <= 1) {
      viewLink = "";
    } else viewLink = linkText;

    const innerContent = (
      <>
        <AvailabilityStatement
          dense={dense}
          count={count}
          availableStatus={status}
          severity={severity}
          locationName={!hasCopies && resource.serial ? undefined : whereText}
          viewLink={viewLink}
        />
      </>
    );

    return (
      <Typography>
        <Link to={`/resource/${resource.id}/copies`}>{innerContent}</Link>
      </Typography>
    );
  }, [
    ready,
    itemAvailability,
    resource.serial,
    resource.id,
    translate,
    dense,
    whereText,
    displayInCopiesBranchIds.length,
    selectedBranchIds.length,
    shelfLocationBranch,
  ]);

  //   reserve and serial have different handling apparently
  if (ready && !!availabilityLink) {
    return availabilityLink;
  }
  return <LSResultItemAvailabilitySkeleton />;
};
